import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { object, string } from 'yup';
import FieldWrap from '../components/form/FieldWrap';
import Input from '../components/form/Input';
import Label from '../components/form/Label';
import Validation from '../components/form/Validation';
import Icon from '../components/icon/Icon';
import PageWrapper from '../components/layouts/PageWrapper/PageWrapper';
import Button from '../components/ui/Button';
import { useApiReload } from '../context/apiRelaod';
import { useAuth } from '../context/authContext';
import { addPatient } from '../services/apiService';

export type TValues = {
	firstName: string;
	lastName: string;
	email: string;
	mobile: number | string | '';
	patientType: string;
};
interface AddPatientPageProps {
	setIsOpen: (open: boolean) => void;
}

const validationSchema = object({
	firstName: string()
		.min(3)
		.max(24)
		.matches(/^[A-Za-z\s]+$/, 'Only letters and spaces are allowed')
		.required('Required'),
	lastName: string()
		.min(3)
		.max(24)
		.matches(/^[A-Za-z\s]+$/, 'Only letters and spaces are allowed')
		.required('Required'),
	email: string()
		.email('Invalid email address')
		.matches(
			/^[^\s@]+@[^\s@]+\.[^\s@]+$/,
			'invaild email address'),
	mobile: string()
		.matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
});

const AddPatientPage: React.FC<AddPatientPageProps> = ({ setIsOpen }) => {
	const { loading, call } = addPatient();
	const navigate = useNavigate();
	const { onLogout } = useAuth();

	const { setReloadPatient, setcaseInfo, setPatientID, patientID } = useApiReload();

	const formik = useFormik({
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			mobile: '',
			patientType: 'NORMAL',
		},
		validationSchema,
		onSubmit: (values: TValues, { setFieldError }) => {
			// Prepare data for API with default values if fields are empty
			const submissionData = {
				...values,
				email: values.email || 'na@na.com', // Default email if empty
				mobile: values.mobile || '0000000000', // Default mobile if empty
			};

			call(submissionData).subscribe({
				next: (res: any) => {
					formik.resetForm();
					toast.success(res.msg);
					setIsOpen(false);
					setReloadPatient(true);
				},
				error: (e: any) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.data?.msg || e.message);
				},
			});
		},
	});

	return (
		<PageWrapper
			isBeforeLoginRoute={false}
			isProtectedRoute={true}
			className='bg-white dark:bg-inherit'
			name='Add Patient'>
			<div className='flex h-full items-center justify-center'>
				<div className='flex w-full flex-col gap-8 px-5 pb-0 pt-5 md:px-7 md:pt-7'>
					<div>
						<span className='text-4xl font-semibold'>Add Patient</span>
					</div>
					<form className='flex flex-col gap-4' noValidate>
						<div>
							<Label htmlFor='firstname'>First Name</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.firstName}
								invalidFeedback={formik.errors.firstName}>
								<FieldWrap firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
									<Input
										dimension='default'
										id='firstName'
										autoComplete='firstName'
										name='firstName'
										placeholder='First Name'
										value={formik.values.firstName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='Lastname'>Last Name</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.lastName}
								invalidFeedback={formik.errors.lastName}>
								<FieldWrap firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
									<Input
										dimension='default'
										id='lastName'
										autoComplete='lastName'
										name='lastName'
										placeholder='Last Name'
										value={formik.values.lastName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='email'>Email</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.email}
								invalidFeedback={formik.errors.email}>
								<FieldWrap
									firstSuffix={<Icon icon='HeroEnvelope' className='mx-2' />}>
									<Input
										dimension='default'
										id='email'
										autoComplete='email'
										name='email'
										placeholder='Email'
										value={formik.values.email}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='mobile'>Mobile Number</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.mobile}
								invalidFeedback={formik.errors.mobile}>
								<FieldWrap firstSuffix={<Icon icon='HeroPhone' className='mx-2' />}>
									<Input
										dimension='default'
										id='mobile'
										autoComplete='mobile'
										name='mobile'
										placeholder='Mobile'
										value={formik.values.mobile}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>

						<div>
							<Button
								isLoading={loading}
								size='lg'
								variant='solid'
								className='w-full font-semibold primary-bg'
								onClick={() => formik.handleSubmit()}>
								Create
							</Button>
						</div>
						<div className='flex items-center justify-center gap-10'>
							<hr className='w-16 border-black' />
							<h5 className='text-md font-bold'>OR</h5>
							<hr className='w-16 border-black' />
						</div>
						<div>
							<Button
								// isLoading={loading}
								size='lg'
								variant='solid'
								className='w-full font-semibold primary-bg'
								onClick={() => {
									setIsOpen(false);
									setPatientID(null);
									setcaseInfo(null);
									navigate(`/patients/cases/new-note-case/Anonymous`, {});
								}}>
								Create anonymous Note
							</Button>
						</div>
					</form>
				</div>
			</div>
		</PageWrapper>
	);
};

export default AddPatientPage;
